import { useIntegrationApp } from '@integration-app/react';
import { format } from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Option } from 'src/components/SelectBox';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest, { AuthedRequest } from 'src/hooks/useAuthedRequest';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { Agent } from 'src/interfaces/agent.interface';
import { DayOfWeek } from 'src/interfaces/workflows.interface';
import {
  Campaign,
  CampaignAlreadyExistsError,
  CampaignValidationError,
  CommunicationChannel,
  CrmCampaignConfig,
  EmailCampaignConfig,
  FollowUpCommunicationConfig,
  SmsCampaignConfig,
  UpdateCampaignDto,
} from 'src/services/campaigns/campaigns.interface';
import { getCampaignAgentIds } from 'src/services/campaigns/campaigns.service';
import {
  IntegrationAppActionKey,
  IntegrationKey,
} from 'src/services/integration-app.service';
import { processPhoneNumber } from 'src/utils/number';
import {
  isEncodedTagValue,
  decodeTagValue,
} from 'src/components/tag/tag.helpers';
import { dateToIsoString, isoStringToDate } from 'src/utils/timeZoneUtils';

export const EVENT_EXPRESSION_ROOT = 'event';

export type FollowUpCommunicationForm = {
  id: string;
  daysAfter?: number;
  contactVia: CommunicationChannel;

  // Phone call related properties
  agentOption?: Option;

  // Email related properties
  emailSubject?: string;
  emailBody?: string;

  // SMS related properties
  smsBody?: string;
};

type CallAutomationScheduleForm = {
  campaignStartDate: string;
  days: DayOfWeek[];
  startTime: string;
  endTime: string;
  timeZoneOption: Option;
};

export type CampaignForm = {
  name: string;
  referenceId: string;
  description?: string;
  contactWindowId?: string;
  schedule: CallAutomationScheduleForm;
  initialCallAfterOption: Option;
  agentOption?: Option;
  fromNumberOptions: Option[];
  followUpCommunications: FollowUpCommunicationForm[];
  crmProviderOption: Option;
  emailProviderOption: Option;
  smsProviderOption: Option;
  recordCommunicationsInCrm?: boolean;
  fromEmail?: string;
  smsFromNumber?: string;
};

export const initialCallAfterOptions: Option[] = [
  { value: '0', label: 'Call Immediately' },
  { value: '1', label: 'Call 1 Hour Later' },
  { value: '2', label: 'Call 2 Hours Later' },
  { value: '4', label: 'Call 4 Hours Later' },
  { value: '24', label: 'Call 1 Day Later' },
  { value: '168', label: 'Call 1 Week Later' },
];

export const timeZoneOptions: Option[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
];

export const CRM_PROVIDER_OPTIONS: Option[] = [
  { value: IntegrationKey.HUBSPOT, label: 'Hubspot' },
  { value: IntegrationKey.SALESFORCE, label: 'Salesforce' },
  { value: IntegrationKey.GOHIGHLEVEL, label: 'GoHighLevel' },
  { value: IntegrationKey.LEADDOCKET, label: 'Lead Docket' },
  { value: IntegrationKey.MONDAY, label: 'Monday.com' },
  { value: IntegrationKey.TLD_CRM, label: 'Total Lead Domination' },
  { value: IntegrationKey.ACTIVECAMPAIGN, label: 'ActiveCampaign' },
  { value: IntegrationKey.SALESFORCE_PARDOT, label: 'Salesforce Pardot' },
];

export const CRM_PROVIDER_OPTIONS_WITH_EXTRA_CONFIG = [IntegrationKey.MONDAY];

export const DEFAULT_CRM_PROVIDER_OPTION: Option = {
  label: 'Select a provider',
  value: '',
};

export const DEFAULT_EMAIL_PROVIDER_OPTION: Option = {
  label: 'Select a provider',
  value: '',
};

export const DEFAULT_SMS_PROVIDER_OPTION: Option = {
  label: 'Select a provider',
  value: '',
};

export const AGENT_VALUE_SAME_AS_INITIAL_CALL = 'same';

export const AGENT_OPTION_SAME_AS_INITIAL_CALL: Option = {
  value: AGENT_VALUE_SAME_AS_INITIAL_CALL,
  label: 'Same as initial call',
};

export const generateListElementId = () =>
  Math.random().toString(36).substring(2, 9);

const initialCampaignAutomationState: CampaignForm = {
  name: '',
  referenceId: '',
  description: '',
  schedule: {
    campaignStartDate: getTodayLocal(),
    days: [
      DayOfWeek.monday,
      DayOfWeek.tuesday,
      DayOfWeek.wednesday,
      DayOfWeek.thursday,
      DayOfWeek.friday,
    ],
    startTime: '11:00',
    endTime: '13:00',
    timeZoneOption: timeZoneOptions[0],
  },
  initialCallAfterOption: initialCallAfterOptions[0],
  agentOption: undefined,
  fromNumberOptions: [],
  followUpCommunications: [],
  crmProviderOption: DEFAULT_CRM_PROVIDER_OPTION,
  emailProviderOption: DEFAULT_EMAIL_PROVIDER_OPTION,
  smsProviderOption: DEFAULT_SMS_PROVIDER_OPTION,
  recordCommunicationsInCrm: false,
  fromEmail: '',
  smsFromNumber: '',
};

type WizardContextType = {
  isEditing: boolean;
  isInitializing: boolean;
  currentStep: number;
  campaignAutomation: CampaignForm;
  campaign: Campaign | null;

  nextStep: () => void;
  prevStep: () => void;
  resetWizard: () => void;
  setCampaignAutomation: (automation: CampaignForm) => void;
  saveCampaignAutomation: () => Promise<Campaign | undefined>;
  setCampaign: (campaign: Campaign) => void;

  // Email provider related properties
  emailProviders: Option[];

  // SMS provider related properties
  smsProviders: Option[];
};

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export function getTodayLocal() {
  return format(new Date(), 'yyyy-MM-dd');
}

const fetchAgentsById = async (
  agentIds: string[],
  authedRequest: AuthedRequest,
): Promise<Agent[]> => {
  return Promise.all(
    agentIds.map((agentId) => authedRequest.get(`/agents/${agentId}`)),
  );
};

export function WizardProvider({
  children,
  isEditing = false,
  editReferenceId,
}: {
  children: React.ReactNode;
  isEditing?: boolean;
  editReferenceId?: string;
}) {
  const location = useLocation();
  const isBeta = location.pathname.includes('/beta');

  const [currentStep, setCurrentStep] = useState(1);
  const [campaignAutomation, setCampaignAutomation] = useState<CampaignForm>({
    ...initialCampaignAutomationState,
  });
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [isEditingStateInitialized, setIsEditingStateInitialized] =
    useState(false);
  const [isEmailProvidersInitialized, setIsEmailProvidersInitialized] =
    useState(false);
  const [isSmsProvidersInitialized, setIsSmsProvidersInitialized] =
    useState(false);
  const authedRequest = useAuthedRequest();
  const campaignsService = useCampaigns();
  const notification = useNotification();
  const hasInitiatedCampaignLoad = useRef(false);

  // Email provider state
  const [emailProviders, setEmailProviders] = useState<Option[]>([]);

  // SMS provider state
  const [smsProviders, setSmsProviders] = useState<Option[]>([]);

  const integrationApp = useIntegrationApp();

  // Load email providers
  const getEmailProviders = async () => {
    try {
      const actions = await integrationApp?.actions.findAll();
      const sendEmailAction = actions?.find(
        (action) => action.key === IntegrationAppActionKey.SEND_EMAIL,
      );
      if (sendEmailAction) {
        const action = await integrationApp?.action(sendEmailAction.id).get();
        const integrations = action?.appliedToIntegrations ?? [];
        setEmailProviders(
          integrations.map(({ integration }) => ({
            label: integration.name,
            value: integration.key,
          })),
        );
      }
      setIsEmailProvidersInitialized(true);
    } catch (error) {
      console.error('Failed to load email providers:', error);
      notification.error(
        'Failed to load email providers. Some features may be unavailable.',
      );
      setIsEmailProvidersInitialized(true); // Still mark as initialized to prevent infinite loading
    }
  };

  const getSmsProviders = async () => {
    try {
      const actions = await integrationApp?.actions.findAll();
      const sendMessageAction = actions?.find(
        (action) => action.key === IntegrationAppActionKey.SEND_MESSAGE,
      );
      if (sendMessageAction) {
        const action = await integrationApp?.action(sendMessageAction.id).get();
        const integrations = action?.appliedToIntegrations ?? [];
        setSmsProviders(
          integrations.map(({ integration }) => ({
            label: integration.name,
            value: integration.key,
          })),
        );
      }
      setIsSmsProvidersInitialized(true);
    } catch (error) {
      console.error('Failed to load SMS providers:', error);
      notification.error(
        'Failed to load SMS providers. Some features may be unavailable.',
      );
      setIsSmsProvidersInitialized(true); // Still mark as initialized to prevent infinite loading
    }
  };

  useEffect(() => {
    getEmailProviders();
    getSmsProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing campaign when editing
  useEffect(() => {
    const loadCampaign = async () => {
      if (
        isEditing &&
        editReferenceId &&
        !isEditingStateInitialized &&
        !hasInitiatedCampaignLoad.current
      ) {
        hasInitiatedCampaignLoad.current = true;
        try {
          const existingCampaign =
            await campaignsService.fetchCampaign(editReferenceId);

          // Extract all the agents referenced in the campaign
          const agentIds = getCampaignAgentIds(existingCampaign);
          const campaignAgents = await fetchAgentsById(agentIds, authedRequest);

          initializePrimaryCampaignFormProperties(
            existingCampaign,
            campaignAgents,
          );
          setCampaign(existingCampaign);
        } catch (error) {
          console.error('Failed to load campaign:', error);
          notification.error('Failed to load campaign');
          setIsEditingStateInitialized(true);
        }
      }
    };

    loadCampaign();
  }, [
    isEditing,
    editReferenceId,
    isEditingStateInitialized,
    authedRequest,
    notification,
    campaignsService,
  ]);

  const initializeCommunicationProviderOptions = useCallback(
    (campaign: Campaign, emailProviders: Option[], smsProviders: Option[]) => {
      const { emailCampaignConfig: emailConfig, smsCampaignConfig: smsConfig } =
        campaign;

      let emailProviderOption = DEFAULT_EMAIL_PROVIDER_OPTION;
      let smsProviderOption = DEFAULT_SMS_PROVIDER_OPTION;

      // Get email provider from campaign
      if (emailConfig?.provider) {
        const emailProvider = emailProviders.find(
          (option) => option.value === emailConfig?.provider,
        );
        emailProviderOption = emailProvider || DEFAULT_EMAIL_PROVIDER_OPTION;
      }

      // Get SMS provider from the campaign
      if (smsConfig?.provider) {
        const smsProvider = smsProviders.find(
          (option) => option.value === smsConfig?.provider,
        );
        smsProviderOption = smsProvider || DEFAULT_SMS_PROVIDER_OPTION;
      }

      setCampaignAutomation({
        ...campaignAutomation,
        emailProviderOption,
        smsProviderOption,
      });
    },
    [campaignAutomation],
  );

  // This is the final step of initializng the edit state. This has to be done after the email and SMS providers
  // are initialized
  useEffect(() => {
    const loadCommunicationProviderOptions = async () => {
      if (
        isEditing &&
        campaign &&
        isEmailProvidersInitialized &&
        isSmsProvidersInitialized &&
        !isEditingStateInitialized
      ) {
        initializeCommunicationProviderOptions(
          campaign,
          emailProviders,
          smsProviders,
        );
        setIsEditingStateInitialized(true);
      }
    };
    loadCommunicationProviderOptions();
  }, [
    isEditing,
    campaign,
    isEditingStateInitialized,
    isEmailProvidersInitialized,
    isSmsProvidersInitialized,
    emailProviders,
    smsProviders,
    initializeCommunicationProviderOptions,
  ]);

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setCampaignAutomation({ ...initialCampaignAutomationState });
    setCampaign(null);
  };

  const initializePrimaryCampaignFormProperties = (
    fetchedCampaign: Campaign,
    campaignAgents: Agent[],
  ) => {
    const { schedule } = fetchedCampaign;

    const timeZoneOption = timeZoneOptions.find(
      (option) => option.value === schedule.timeZone,
    );

    const campaignStartDateNonIso = schedule.campaignStartDate
      ? isoStringToDate(schedule.campaignStartDate, schedule.timeZone)
      : null;

    const initialCallAfterOption =
      initialCallAfterOptions.find(
        (option) =>
          option.value ===
          String(fetchedCampaign.initialCallConfig.scheduleAfterHours),
      ) || initialCallAfterOptions[0];

    const fromNumberOptions: Option[] =
      fetchedCampaign.callCampaignConfig.fromNumbers.map((tagOrNumber) => {
        if (isEncodedTagValue(tagOrNumber)) {
          return {
            value: tagOrNumber,
            label: decodeTagValue(tagOrNumber),
          };
        }
        return {
          value: tagOrNumber,
          label: processPhoneNumber(tagOrNumber),
        };
      });

    const initialCallAgentOption: Option | undefined = {
      value: fetchedCampaign.initialCallConfig.agentId,
      label:
        campaignAgents.find(
          (a) => a.id === fetchedCampaign.initialCallConfig.agentId,
        )?.name || (fetchedCampaign.initialCallConfig.agentId as string),
    };

    const followUpCommunications: FollowUpCommunicationForm[] = [];
    let followupCount = 0;
    for (const followupConfig of fetchedCampaign.followUpCommunicationConfigs) {
      const daysAfter = followupConfig.scheduleAfterDays;
      followupCount++;

      if (followupConfig.contactVia === CommunicationChannel.EMAIL) {
        followUpCommunications.push({
          id: `followup-${followupCount}`,
          daysAfter,
          contactVia: CommunicationChannel.EMAIL,
          emailBody: followupConfig.emailConfig?.body,
          emailSubject: followupConfig.emailConfig?.subject,
        });
      } else if (followupConfig.contactVia === CommunicationChannel.SMS) {
        followUpCommunications.push({
          id: `followup-${followupCount}`,
          daysAfter,
          contactVia: CommunicationChannel.SMS,
          smsBody: followupConfig.smsConfig?.body,
        });
      } else {
        // This is a call step
        const agentId = followupConfig.callConfig?.agentId;
        let agentOption = AGENT_OPTION_SAME_AS_INITIAL_CALL;
        if (agentId !== initialCallAgentOption?.value) {
          agentOption = {
            value: agentId as string,
            label:
              campaignAgents.find((a) => a.id === agentId)?.name ||
              (agentId as string),
          };
        }

        followUpCommunications.push({
          id: `followup-${followupCount}`,
          daysAfter,
          contactVia: CommunicationChannel.CALL,
          agentOption,
        });
      }
    }

    let crmProviderOption = DEFAULT_CRM_PROVIDER_OPTION;
    let recordCommunicationsInCrm = false;
    if (fetchedCampaign.crmCampaignConfig !== undefined) {
      const crmConfig = fetchedCampaign.crmCampaignConfig;
      const crmProvider = CRM_PROVIDER_OPTIONS.find(
        (option) => option.value === crmConfig.provider,
      );
      crmProviderOption = crmProvider || DEFAULT_CRM_PROVIDER_OPTION;
      recordCommunicationsInCrm = crmConfig.recordCommunications;
    }

    setCampaignAutomation((prev: CampaignForm) => ({
      ...prev,
      name: fetchedCampaign.name,
      referenceId: fetchedCampaign.referenceId,
      description: fetchedCampaign.description,
      schedule: {
        ...prev.schedule,
        campaignStartDate:
          campaignStartDateNonIso ?? prev.schedule.campaignStartDate,
        days: fetchedCampaign.schedule.days,
        startTime: fetchedCampaign.schedule.startTime,
        endTime: fetchedCampaign.schedule.endTime,
        timeZoneOption: timeZoneOption || timeZoneOptions[0],
      },
      initialCallAfterOption,
      agentOption: initialCallAgentOption,
      fromNumberOptions,
      followUpCommunications,
      crmProviderOption,
      // emailProviderOption - filled in later
      // smsProviderOption - filled in later
      recordCommunicationsInCrm,
      fromEmail: fetchedCampaign.emailCampaignConfig?.fromEmail,
      smsFromNumber: fetchedCampaign.smsCampaignConfig?.fromNumber,
    }));
  };

  const buildCampaign = (isEditing: boolean): UpdateCampaignDto => {
    const initialAgentid = campaignAutomation.agentOption?.value as string;

    const campaignStartDateIso = dateToIsoString(
      campaignAutomation.schedule.campaignStartDate,
      campaignAutomation.schedule.timeZoneOption.value,
    );

    const schedule = {
      campaignStartDate: campaignStartDateIso,
      days: campaignAutomation.schedule.days,
      startTime: campaignAutomation.schedule.startTime,
      endTime: campaignAutomation.schedule.endTime,
      timeZone: campaignAutomation.schedule.timeZoneOption.value,
    };

    let hasEmailFollowup = false;
    let hasSmsFollowup = false;
    const followUpCommunicationConfigs: FollowUpCommunicationConfig[] =
      campaignAutomation.followUpCommunications.map((communication) => {
        if (communication.daysAfter === undefined) {
          throw new CampaignValidationError(
            'Days after is required for follow up communications',
          );
        }

        if (communication.contactVia === CommunicationChannel.EMAIL) {
          hasEmailFollowup = true;
          return {
            scheduleAfterDays: communication.daysAfter,
            contactVia: CommunicationChannel.EMAIL,
            emailConfig: {
              subject: communication.emailSubject || '',
              body: communication.emailBody || '',
            },
          };
        } else if (communication.contactVia === CommunicationChannel.SMS) {
          hasSmsFollowup = true;
          return {
            scheduleAfterDays: communication.daysAfter,
            contactVia: CommunicationChannel.SMS,
            smsConfig: {
              body: communication.smsBody || '',
            },
          };
        } else {
          let agentId = communication.agentOption?.value as string;
          if (agentId === AGENT_VALUE_SAME_AS_INITIAL_CALL) {
            agentId = initialAgentid;
          }

          return {
            scheduleAfterDays: communication.daysAfter,
            contactVia: CommunicationChannel.CALL,
            callConfig: {
              agentId,
            },
          };
        }
      });

    const emailCampaignConfig: EmailCampaignConfig | undefined =
      hasEmailFollowup
        ? {
            provider: campaignAutomation.emailProviderOption.value,
            fromEmail: campaignAutomation.fromEmail || '',
          }
        : undefined;

    const smsCampaignConfig: SmsCampaignConfig | undefined = hasSmsFollowup
      ? {
          provider: campaignAutomation.smsProviderOption.value,
          fromNumber: campaignAutomation.smsFromNumber || '',
        }
      : undefined;

    const crmCampaignConfig: CrmCampaignConfig | undefined = campaignAutomation
      .crmProviderOption.value
      ? {
          provider: campaignAutomation.crmProviderOption.value,
          recordCommunications:
            campaignAutomation.recordCommunicationsInCrm || false,
        }
      : undefined;

    const payload: UpdateCampaignDto = {
      referenceId: campaignAutomation.referenceId,
      name: campaignAutomation.name,
      description: campaignAutomation.description,

      schedule,

      initialCallConfig: {
        agentId: initialAgentid,
        scheduleAfterHours: parseInt(
          campaignAutomation.initialCallAfterOption.value,
          10,
        ),
      },
      followUpCommunicationConfigs,

      callCampaignConfig: {
        fromNumbers: campaignAutomation.fromNumberOptions.map(
          (option) => option.value,
        ),
      },
      emailCampaignConfig,
      smsCampaignConfig,
      crmCampaignConfig,
    };

    // Thread through contactWindowId if present on the automation state
    if (campaignAutomation.contactWindowId) {
      payload.contactWindowId = campaignAutomation.contactWindowId;
    }

    if (!isEditing && isBeta) {
      payload.schedule.campaignStartDate = undefined as unknown as string;
    }

    return payload;
  };

  const trackEmailFromCampaign = async (campaign: Campaign): Promise<void> => {
    try {
      const fromEmail = campaign.emailCampaignConfig?.fromEmail;
      if (fromEmail) {
        await authedRequest.post('/emails/recent', {
          email: fromEmail.trim().toLowerCase(),
        });
      }
    } catch (error) {
      // Email tracking is not critical - don't fail the campaign operation
      console.warn('Failed to track email from campaign:', error);
    }
  };

  const saveCampaignAutomation = async (): Promise<Campaign | undefined> => {
    try {
      const campaignPayload = buildCampaign(isEditing);
      if (isEditing) {
        const updatedCampaign =
          await campaignsService.updateCampaign(campaignPayload);

        setCampaign(updatedCampaign);

        // Track email address for recent emails
        await trackEmailFromCampaign(updatedCampaign);

        return updatedCampaign;
      } else {
        try {
          const createdCampaign =
            await campaignsService.createCampaign(campaignPayload);
          setCampaign(createdCampaign);
          await trackEmailFromCampaign(createdCampaign);
          return createdCampaign;
        } catch (error) {
          if (error instanceof CampaignAlreadyExistsError) {
            notification.error(
              'A campaign already exists with this ID. Please enter a unique Campaign ID.',
            );
          } else {
            notification.error('Failed to save campaign');
            console.error('Failed to save campaign:', error);
          }
        }
      }
    } catch (error) {
      if (error instanceof CampaignValidationError) {
        notification.error(error.message);
      } else {
        console.error('Error saving campaign:', error);
        notification.error(
          isEditing ? 'Failed to update campaign' : 'Failed to create campaign',
        );
      }
    }

    return undefined;
  };

  return (
    <WizardContext.Provider
      value={{
        isEditing,
        isInitializing:
          (isEditing && !isEditingStateInitialized) ||
          !isEmailProvidersInitialized ||
          !isSmsProvidersInitialized,
        currentStep,
        campaignAutomation,
        campaign,
        nextStep,
        prevStep,
        resetWizard,
        setCampaignAutomation,
        saveCampaignAutomation,
        setCampaign,
        // Email provider values
        emailProviders,

        // SMS provider values
        smsProviders,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard() {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
